<script lang="ts">
import type { HTMLAttributes } from 'vue'

export interface TimeframePickerOption<T extends string = string> {
  value: T
  label: string
}

export interface TimeframePickerProps<T extends string = string> {
  options: TimeframePickerOption<T>[]
  modelValue: T | null
  disabled?: boolean
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts" generic="T extends string">
import { cn } from '#ui-kit/lib/utils'
import { Button } from '#ui-kit/ui/button'

const props = defineProps<TimeframePickerProps<T>>()

// Emits only option values, so a v-model bound to `T` never receives null.
const emit = defineEmits<{
  'update:modelValue': [value: T]
}>()
</script>

<template>
  <div :class="cn('grid grid-cols-2 gap-2', props.class)">
    <Button
      v-for="option in props.options"
      :key="option.value"
      size="sm"
      block
      :variant="option.value === props.modelValue ? 'default' : 'outline'"
      :class="{ 'bg-white': option.value !== props.modelValue }"
      :aria-pressed="option.value === props.modelValue"
      :disabled="props.disabled"
      @click="emit('update:modelValue', option.value)"
    >
      {{ option.label }}
    </Button>
  </div>
</template>
